import { useState } from 'react'
import { useDarkMode } from '../App'

interface Request {
  id: string
  title: string
  requester: string
  email: string
  date: string
  status: 'pending' | 'approved' | 'rejected'
  type: 'book' | 'paper' | 'article'
}

// Mock data for demo
const MOCK_REQUESTS: Request[] = [
  {
    id: '1',
    title: 'Advanced Machine Learning Textbook',
    requester: 'John Smith',
    email: 'john@university.edu',
    date: '2025-09-13',
    status: 'pending',
    type: 'book'
  },
  {
    id: '2',
    title: 'Quantum Computing Research Paper',
    requester: 'Alice Johnson',
    email: 'alice.j@research.org',
    date: '2025-09-12',
    status: 'approved',
    type: 'paper'
  },
  {
    id: '3',
    title: 'Climate Change Analysis',
    requester: 'Bob Wilson',
    email: 'bob.wilson@climate.gov',
    date: '2025-09-11',
    status: 'rejected',
    type: 'article'
  }
]

export default function AdminRequests(){
  const { isDark } = useDarkMode()
  const [requests, setRequests] = useState<Request[]>(MOCK_REQUESTS)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all')

  const filteredRequests = requests.filter(req => 
    filter === 'all' || req.status === filter
  )

  const handleStatusChange = (id: string, newStatus: 'approved' | 'rejected') => {
    setRequests(prev => 
      prev.map(req => 
        req.id === id ? { ...req, status: newStatus } : req
      )
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-800'
      case 'approved':
        return isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-800'
      case 'rejected':
        return isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-800'
      default:
        return isDark ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className={`min-h-screen w-full transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>
      {/* Hero Section - consistent with other pages */}
      <div className="relative overflow-hidden">
        <div className={`absolute inset-0 transition-colors ${
          isDark ? 'bg-gradient-to-br from-emerald-900/10 to-cyan-900/10' : 'bg-gradient-to-br from-blue-50/30 to-purple-50/30'
        }`}></div>
        <div className="relative max-w-7xl mx-auto px-4 py-20">
          <div className="text-center">
            <div className={`inline-flex items-center px-4 py-2 border rounded-full text-sm font-medium mb-8 transition-colors ${
              isDark 
                ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' 
                : 'bg-blue-50 border-blue-200 text-blue-600'
            }`}>
              <svg className={`w-4 h-4 mr-2 ${isDark ? 'text-emerald-400' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Admin Dashboard
            </div>
            
            <h1 className={`text-4xl md:text-5xl font-bold mb-6 transition-colors ${
              isDark 
                ? 'bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent' 
                : 'text-gray-900'
            }`}>
              Requests Management
            </h1>
            <p className={`text-xl mb-12 max-w-2xl mx-auto transition-colors ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Review and manage user requests for library resources
            </p>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className={`border rounded-xl p-6 text-center transition-colors ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className={`text-2xl font-bold mb-1 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
              {requests.filter(r => r.status === 'pending').length}
            </div>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pending</div>
          </div>
          <div className={`border rounded-xl p-6 text-center transition-colors ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className={`text-2xl font-bold mb-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
              {requests.filter(r => r.status === 'approved').length}
            </div>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Approved</div>
          </div>
          <div className={`border rounded-xl p-6 text-center transition-colors ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className={`text-2xl font-bold mb-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
              {requests.filter(r => r.status === 'rejected').length}
            </div>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Rejected</div>
          </div>
          <div className={`border rounded-xl p-6 text-center transition-colors ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className={`text-2xl font-bold mb-1 ${isDark ? 'text-emerald-400' : 'text-blue-600'}`}>
              {requests.length}
            </div>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-8">
          {['all', 'pending', 'approved', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status as any)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors capitalize ${
                filter === status
                  ? (isDark 
                      ? 'bg-emerald-600 text-white' 
                      : 'bg-blue-600 text-white')
                  : (isDark
                      ? 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700'
                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50')
              }`}
            >
              {status} ({status === 'all' ? requests.length : requests.filter(r => r.status === status).length})
            </button>
          ))}
        </div>

        {/* Requests Table */}
        <div className={`border rounded-xl overflow-hidden transition-colors ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className="text-lg font-semibold">Recent Requests</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Resource
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Requester
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Date
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {filteredRequests.map((request) => (
                  <tr key={request.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium">{request.title}</div>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {request.type}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium">{request.requester}</div>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {request.email}
                        </div>
                      </div>
                    </td>
                    <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                      {new Date(request.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {request.status === 'pending' && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleStatusChange(request.id, 'approved')}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                              isDark
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : 'bg-green-100 hover:bg-green-200 text-green-800'
                            }`}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleStatusChange(request.id, 'rejected')}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                              isDark
                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                : 'bg-red-100 hover:bg-red-200 text-red-800'
                            }`}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty State */}
        {filteredRequests.length === 0 && (
          <div className="text-center py-12">
            <svg className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2m16-7V9a2 2 0 00-2-2H6a2 2 0 00-2 2v4.01" />
            </svg>
            <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              No {filter === 'all' ? '' : filter} requests
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              {filter === 'all' 
                ? 'No requests have been submitted yet' 
                : `No ${filter} requests at this time`}
            </p>
          </div>
        )}
        
        {/* Note about data source */}
        <div className={`mt-8 p-4 rounded-xl border transition-colors ${
          isDark 
            ? 'bg-gray-800/50 border-gray-700 text-gray-400' 
            : 'bg-blue-50 border-blue-200 text-blue-700'
        }`}>
          <div className="flex items-start">
            <svg className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="font-medium mb-1">Development Note</h4>
              <p className="text-sm">This page is protected by SWA roles. Wire your data source when ready. Currently showing mock data for demonstration purposes.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}